import {Form, Input, Row} from 'antd';
import FormItem from 'antd/es/form/FormItem';
import {isMatch} from 'lodash-es';
import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {DirectoriesCompanyFormCard} from 'components/directories-company-form/directories-company-form-card/directories-company-form-card';
import {DIRECTORIES_COMPANY_NAME} from 'components/directories-company-form/directories-company-form.constants';
import {
    TDirectoriesCompanyDto,
    IDirectoriesCompanyFormTabProps,
} from 'components/directories-company-form/directories-company-form.types';
import {DateCell} from 'components/table/columns/date-cell/date-cell';
import {loadFormData, resetLoadedData} from 'modules/data';
import {actionRequestData} from 'modules/data/data-actions';
import {selectFormEntityData} from 'modules/data/data-selectors';
import {IDatePickerState} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {TStateSetter} from 'shared/types/generics';
import {ERequestType} from 'shared/types/requests';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import './common-info.less';

interface ICommonInfoProps extends IDirectoriesCompanyFormTabProps {
    entityId: string | null | undefined;
    entityName: string;
    setLoading: TStateSetter<boolean>;
}

export const CommonInfo = ({
    entityId,
    entityName,
    form,
    isEntityExists,
    setLoading,
    setDirty,
    resetFormStatus,
    isDisabled,
}: ICommonInfoProps) => {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const formData = useAppSelector(selectFormEntityData(entityName))?.data as unknown as TDirectoriesCompanyDto;

    const [initialValues, setInitialValues] = useState<TDirectoriesCompanyDto | undefined>(formData);

    const datePickerStates: IDatePickerState = {};

    useEffect(() => {
        if (entityId && isEntityExists && !formData) {
            setLoading(true);
            dispatch(loadFormData(entityName, entityId))
                .finally(() => setLoading(false));
        }

        if (formData && !initialValues) setInitialValues(formData);
    }, [formData, initialValues, isEntityExists]);

    useEffect(() => {
        if (initialValues) form.resetFields();
    }, [initialValues]);

    useEffect(() => () => {
        dispatch(resetLoadedData(entityName, EEntityType.FORM));
    }, []);

    const handleSubmit = (values: TDirectoriesCompanyDto) => {
        setLoading(true);
        actionRequestData({
            requestUrl: `/${DIRECTORIES_COMPANY_NAME}${isEntityExists ? `/${entityId}` : ''}`,
            requestType: isEntityExists ? ERequestType.PUT : ERequestType.POST,
            data: values,
        }).then(res => {
            if (res.status === 200) {
                resetFormStatus();

                const createdEntityId = res?.data?.entityId;
                if (!isEntityExists && createdEntityId) {
                    history.replace(`/${DIRECTORIES_COMPANY_NAME}?entity=${createdEntityId}`);
                } else {
                    dispatch(resetLoadedData(entityName, EEntityType.FORM));
                }
            }
        }).finally(() => setLoading(false));
    };

    const handleValuesChange = () => {
        const currentValues = form.getFieldsValue();

        const areDifferent = initialValues === undefined || !isMatch(initialValues, currentValues);
        setDirty(areDifferent);
    };

    return (
        <div className="common-info">
            <Form
                className="common-info-form"
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
                onValuesChange={handleValuesChange}
                initialValues={initialValues}
            >
                <DirectoriesCompanyFormCard
                    title="Основная информация"
                >
                    <FormItem
                        name="name"
                        label="Наименование"
                        rules={[
                            {required: true},
                            {
                                pattern: /^[а-яА-ЯёЁ0-9 !"#$%'()*+,-./:;<=>?@[\]^_`{|}~«»\-_—№&]+$/,
                                message: 'Данное поле может содержать только кириллицу, цифры и символы',
                            },
                            {
                                pattern: /^.{1,300}$/,
                                message: 'Количество знаков в данном поле должно быть не более 300',
                            },
                        ]}
                    >
                        <Input
                            placeholder="Введите наименование"
                            disabled={isDisabled}
                        />
                    </FormItem>
                    <Row>
                        <FormItem
                            name="organizationCode"
                            label="Код"
                            rules={[
                                {required: true},
                                {
                                    pattern: /^.{1,20}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 20',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите код"
                                disabled={isEntityExists || isDisabled}
                            />
                        </FormItem>
                        <FormItem
                            name="externalCode"
                            label="Внешний код"
                            rules={[
                                {
                                    pattern: /^.{1,70}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 70',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите внешний код"
                                disabled={isDisabled}
                            />
                        </FormItem>
                    </Row>
                    <Row>
                        <FormItem
                            name="inn"
                            label="ИНН"
                            rules={[
                                {required: true},
                                {
                                    pattern: /^.{1,10}$/,
                                    message: (
                                        'Количество знаков в данном поле должно быть не более 10.'
                                        + ' Формат ввода: 4581562555'
                                    ),
                                },
                                {
                                    pattern: /^[0-9]+$/,
                                    message: 'Данное поле может содержать только цифры',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите ИНН"
                                disabled={isDisabled}
                            />
                        </FormItem>
                        <FormItem
                            name="kpp"
                            label="КПП"
                            rules={[
                                {
                                    pattern: /^.{1,9}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 9.'
                                        + ' Формат ввода: 772101001',
                                },
                                {
                                    pattern: /^[0-9]+$/,
                                    message: 'Данное поле может содержать только цифры. Формат ввода: 455756255',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите КПП"
                                disabled={isDisabled}
                            />
                        </FormItem>
                    </Row>
                    <Row
                        justify="space-between"
                    >
                        <FormItem
                            name="kpp2"
                            label="КПП2"
                            rules={[
                                {
                                    pattern: /^.{1,30}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 30',
                                },
                                {
                                    pattern: /^[0-9]+$/,
                                    message: 'Данное поле должно содержать только цифры',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите код"
                                disabled={isDisabled}
                            />
                        </FormItem>
                        <FormItem
                            name="okved"
                            label="ОКВЭД"
                            rules={[
                                {
                                    pattern: /^.{1,30}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 30',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите ОКВЭД"
                                disabled={isDisabled}
                            />
                        </FormItem>
                    </Row>
                    <Row
                        justify="space-between"
                    >
                        <FormItem
                            name="okpo"
                            label="ОКПО"
                            rules={[
                                {
                                    pattern: /^.{1,10}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 10',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите ОКПО"
                                disabled={isDisabled}
                            />
                        </FormItem>
                        <FormItem
                            name="ogrn"
                            label="ОГРН"
                            rules={[
                                {
                                    pattern: /^.{1,18}$/,
                                    message: 'Количество знаков в данном поле должно быть не более 18',
                                },
                                {
                                    pattern: /^[0-9][-][0-9]{2}[-][0-9]{2}[-][0-9]{2}[-][0-9]{5}[-][0-9]$/,
                                    message: 'Данное поле должно содержать 13 цифр и 5 "-".'
                                        + ' Формат ввода: 1-02-66-05-60662-0',
                                },
                            ]}
                        >
                            <Input
                                placeholder="Введите ОГРН"
                                disabled={isDisabled}
                            />
                        </FormItem>
                    </Row>
                    <FormItem
                        name="organizationFnsName"
                        label="Наименование НО"
                        rules={[
                            {
                                pattern: /^.{1,2000}$/,
                                message: 'Количество знаков в данном поле должно быть не более 2000',
                            },
                        ]}
                    >
                        <Input
                            placeholder="Введите наименование НО"
                            disabled={isDisabled}
                        />
                    </FormItem>
                    <FormItem
                        name="endDate"
                        label="Закрыто с"
                        style={{width: '50%'}}
                    >
                        <DateCell
                            format="DD.MM.YYYY"
                            datePickerStates={datePickerStates}
                            errorMessage="Данное поле должно содержать только цифры и символы. Формат ввода: дд.мм.гггг"
                            disabled={isDisabled}
                        />
                    </FormItem>
                </DirectoriesCompanyFormCard>
                <DirectoriesCompanyFormCard
                    title="Контактные данные"
                >
                    <FormItem
                        name="contactPerson"
                        label="Представитель"
                        rules={[
                            {
                                pattern: /^.{1,300}$/,
                                message: 'Количество знаков в данном поле должно быть не более 300',
                            },
                        ]}
                    >
                        <Input
                            placeholder="Введите значение"
                            disabled={isDisabled}
                        />
                    </FormItem>
                    <FormItem
                        name="contactPhone"
                        label="Телефон"
                        rules={[
                            {
                                pattern: /^.{1,12}$/,
                                message: 'Количество знаков в данном поле должно быть не более 12',
                            },
                            {
                                pattern: /^(?:\+7|8)[0-9]+$/,
                                message: (
                                    'Данное поле может содержать только цифры и символ "+".'
                                    + ' Формат ввода: +7 999 999 99 99 или 8 999 999 99 99'
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Введите телефон"
                            disabled={isDisabled}
                        />
                    </FormItem>
                    <FormItem
                        name="postAddress"
                        label="Почтовый адрес"
                        rules={[
                            {
                                pattern: /^.{1,1000}$/,
                                message: 'Количество знаков в данном поле должно быть не более 1000',
                            },
                        ]}
                    >
                        <Input
                            placeholder="Введите почтовый адрес"
                            disabled={isDisabled}
                        />
                    </FormItem>
                    <FormItem
                        name="address"
                        label="Адрес"
                        rules={[
                            {
                                pattern: /^.{1,1000}$/,
                                message: 'Количество знаков в данном поле должно быть не более 1000',
                            },
                        ]}
                    >
                        <Input
                            placeholder="Введите адрес"
                            disabled={isDisabled}
                        />
                    </FormItem>
                </DirectoriesCompanyFormCard>
            </Form>
        </div>
    );
};
